import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FaFingerprint, FaSync, FaSyncAlt, FaTrash, FaPlus, FaTimes, FaPowerOff,
  FaClock, FaDatabase, FaListUl, FaCircle, FaEllipsisV,
} from 'react-icons/fa';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken')}`,
});

const FINGER_OPTIONS = [
  { id: 0, label: 'Right Thumb' },
  { id: 1, label: 'Right Index' },
  { id: 6, label: 'Left Thumb' },
  { id: 7, label: 'Left Index' },
];

const SUB_BUSINESS_OPTIONS = ['Salon', 'Gym', 'Spa', 'General'];

async function apiGet(path) {
  const res = await axios.get(`${API_BASE_URL}${path}`, { headers: getAuthHeaders() });
  return res.data;
}
async function apiPost(path, body = {}) {
  const res = await axios.post(`${API_BASE_URL}${path}`, body, { headers: getAuthHeaders() });
  return res.data;
}
async function apiDelete(path) {
  const res = await axios.delete(`${API_BASE_URL}${path}`, { headers: getAuthHeaders() });
  return res.data;
}

function timeSince(date) {
  if (!date) return 'Never';
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function DeviceManagement({ staff }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openActionMenu, setOpenActionMenu] = useState(null); // serialNumber of the row whose menu is open

  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDevice, setNewDevice] = useState({ serialNumber: '', name: '', location: '' });

  const [enrollModal, setEnrollModal] = useState(null); // { device }
  const [enrollEmployeeId, setEnrollEmployeeId] = useState('');
  const [enrollFingerId, setEnrollFingerId] = useState(0);
  const [enrollState, setEnrollState] = useState('idle'); // idle | sending | pending | acknowledged | failed | error
  const [enrollMessage, setEnrollMessage] = useState('');
  const enrollPollRef = useRef(null);

  const [syncModal, setSyncModal] = useState(null); // { device }
  const [syncEmployeeId, setSyncEmployeeId] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);

  const [queueModal, setQueueModal] = useState(null); // { device, queue }

  const [dataModal, setDataModal] = useState(null); // { device }
  const [deviceData, setDeviceData] = useState({ status: 'idle', users: [] });
  const [selectedPins, setSelectedPins] = useState([]);
  const [importSubBusiness, setImportSubBusiness] = useState('General');
  const [importResults, setImportResults] = useState(null);
  const [dataBusy, setDataBusy] = useState(false);
  const dataPollRef = useRef(null);

  const notify = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 6000); };
  const fail = (err, fallback) => setError((err.response?.data?.message) || err.message || fallback);

  const fetchDevices = useCallback(async () => {
    try {
      const data = await apiGet('/api/device-commands/devices');
      setDevices(data.devices || []);
    } catch (err) {
      fail(err, 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);
  useEffect(() => {
    const interval = setInterval(fetchDevices, 30000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  useEffect(() => () => {
    if (enrollPollRef.current) clearInterval(enrollPollRef.current);
    if (dataPollRef.current) clearInterval(dataPollRef.current);
  }, []);

  // ───────────────────────── Add / Delete device ─────────────────────────
  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      await apiPost('/api/device-commands/devices', newDevice);
      notify('Device added.');
      setShowAddDevice(false);
      setNewDevice({ serialNumber: '', name: '', location: '' });
      fetchDevices();
    } catch (err) {
      fail(err, 'Failed to add device');
    }
  };

  const handleDeleteDevice = async (device) => {
    if (!window.confirm(`Delete "${device.name}" (${device.serialNumber})? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/device-commands/devices/${device.serialNumber}`);
      notify('Device deleted.');
      fetchDevices();
    } catch (err) {
      fail(err, 'Failed to delete device');
    }
  };

  // ───────────────────────── Enroll fingerprint ─────────────────────────
  const openEnroll = (device) => {
    setEnrollModal({ device });
    setEnrollEmployeeId('');
    setEnrollFingerId(0);
    setEnrollState('idle');
    setEnrollMessage('');
  };

  const closeEnroll = () => {
    if (enrollPollRef.current) { clearInterval(enrollPollRef.current); enrollPollRef.current = null; }
    setEnrollModal(null);
  };

  const sendEnroll = async () => {
    if (!enrollEmployeeId) return;
    try {
      setEnrollState('sending');
      const result = await apiPost('/api/device-commands/enroll-fingerprint', {
        deviceSN: enrollModal.device.serialNumber,
        employeeId: enrollEmployeeId,
        fingerId: enrollFingerId,
      });
      setEnrollState('pending');
      setEnrollMessage('Command queued. Waiting for the device to pick it up on its next poll...');

      enrollPollRef.current = setInterval(async () => {
        try {
          const status = await apiGet(`/api/device-commands/status/${enrollModal.device.serialNumber}/${result.commandId}`);
          if (status.status === 'sent') {
            setEnrollMessage('Device received the command — ask the staff member to place their finger now.');
          } else if (status.status === 'acknowledged') {
            setEnrollState('acknowledged');
            setEnrollMessage('Fingerprint enrolled successfully.');
            clearInterval(enrollPollRef.current);
          } else if (status.status === 'failed') {
            setEnrollState('failed');
            setEnrollMessage(
              `Device rejected the command (return code ${status.returnCode}). ` +
              `If this employee hasn't been synced to the device yet, use "Sync Staff" first, then retry.`
            );
            clearInterval(enrollPollRef.current);
          }
        } catch (pollErr) {
          console.error('Error polling enrollment status:', pollErr);
        }
      }, 4000);
    } catch (err) {
      setEnrollState('error');
      setEnrollMessage(err.response?.data?.message || err.message);
    }
  };

  // ───────────────────────── Sync staff (ADD_USER) ─────────────────────────
  const openSync = (device) => {
    setSyncModal({ device });
    setSyncEmployeeId('');
  };

  const handleSyncOne = async () => {
    if (!syncEmployeeId) return;
    try {
      setSyncBusy(true);
      await apiPost('/api/device-commands/sync-staff', { deviceSN: syncModal.device.serialNumber, employeeId: syncEmployeeId });
      notify('Staff member queued to sync to device.');
      setSyncModal(null);
    } catch (err) {
      fail(err, 'Failed to queue sync');
    } finally {
      setSyncBusy(false);
    }
  };

  const handleSyncAll = async () => {
    if (!window.confirm(`Sync all active staff to "${syncModal.device.name}"? This queues one command per staff member.`)) return;
    try {
      setSyncBusy(true);
      const result = await apiPost('/api/device-commands/sync-all-staff', { deviceSN: syncModal.device.serialNumber });
      notify(result.message || 'All staff queued to sync.');
      setSyncModal(null);
    } catch (err) {
      fail(err, 'Failed to queue bulk sync');
    } finally {
      setSyncBusy(false);
    }
  };

  // ───────────────────────── Command queue ─────────────────────────
  const openQueue = async (device) => {
    setQueueModal({ device, queue: [] });
    try {
      const data = await apiGet(`/api/device-commands/devices/${device.serialNumber}/queue`);
      setQueueModal({ device, queue: data.queue || [] });
    } catch (err) {
      fail(err, 'Failed to load command queue');
    }
  };

  // ───────────────────────── Reboot / Sync time ─────────────────────────
  const handleReboot = async (device) => {
    if (!window.confirm(`Reboot "${device.name}"? It will be briefly unavailable.`)) return;
    try {
      await apiPost('/api/device-commands/reboot', { deviceSN: device.serialNumber });
      notify('Reboot queued.');
    } catch (err) {
      fail(err, 'Failed to queue reboot');
    }
  };

  const handleSyncTime = async (device) => {
    try {
      await apiPost('/api/device-commands/sync-time', { deviceSN: device.serialNumber });
      notify('Time sync queued.');
    } catch (err) {
      fail(err, 'Failed to queue time sync');
    }
  };

  // ───────────────────────── View Device Data ─────────────────────────
  const stopDataPolling = () => {
    if (dataPollRef.current) { clearInterval(dataPollRef.current); dataPollRef.current = null; }
  };

  const pollDeviceData = useCallback(async (serialNumber) => {
    try {
      const res = await apiGet(`/api/device-commands/device-data/${serialNumber}/status`);
      setDeviceData(res);
      if (res.status === 'complete' || (res.status === 'requesting' && res.isIdle)) stopDataPolling();
    } catch (err) {
      console.error('Error polling device data:', err);
    }
  }, []);

  const openDeviceData = async (device) => {
    setDataModal({ device });
    setSelectedPins([]);
    setImportResults(null);
    setDeviceData({ status: 'requesting', users: [] });
    try {
      await apiPost(`/api/device-commands/device-data/${device.serialNumber}/retrieve`);
      await pollDeviceData(device.serialNumber);
      dataPollRef.current = setInterval(() => pollDeviceData(device.serialNumber), 3000);
    } catch (err) {
      fail(err, 'Failed to start device data retrieval');
    }
  };

  const closeDeviceData = () => {
    stopDataPolling();
    setDataModal(null);
  };

  const togglePin = (pin) => {
    setSelectedPins((prev) => (prev.includes(pin) ? prev.filter((p) => p !== pin) : [...prev, pin]));
  };

  const toggleSelectAllPins = () => {
    setSelectedPins((prev) => (prev.length === deviceData.users.length ? [] : deviceData.users.map((u) => u.pin)));
  };

  const handleDeleteSelectedFromDevice = async () => {
    if (selectedPins.length === 0) return;
    if (!window.confirm(`Delete ${selectedPins.length} selected user(s) from this device? This cannot be undone.`)) return;
    try {
      setDataBusy(true);
      await apiPost('/api/device-commands/delete-users', { deviceSN: dataModal.device.serialNumber, pins: selectedPins });
      notify(`Queued deletion of ${selectedPins.length} user(s).`);
      setDeviceData((prev) => ({ ...prev, users: prev.users.filter((u) => !selectedPins.includes(u.pin)) }));
      setSelectedPins([]);
    } catch (err) {
      fail(err, 'Failed to queue delete');
    } finally {
      setDataBusy(false);
    }
  };

  const handleImportSelected = async () => {
    if (selectedPins.length === 0) return;
    try {
      setDataBusy(true);
      const res = await apiPost(`/api/device-commands/device-data/${dataModal.device.serialNumber}/import`, {
        pins: selectedPins,
        subBusiness: importSubBusiness,
      });
      setImportResults(res.results || []);
      const created = (res.results || []).filter((r) => r.outcome === 'created').length;
      const conflicts = (res.results || []).filter((r) => r.outcome === 'conflict').length;
      notify(`Imported ${created} new staff record(s)${conflicts > 0 ? `, ${conflicts} conflict(s) need review below` : ''}.`);
    } catch (err) {
      fail(err, 'Failed to import');
    } finally {
      setDataBusy(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading devices...</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Device Management</h2>
        <button
          onClick={() => setShowAddDevice(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <FaPlus size={12} /> Add Device
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{error}</p>}
      {success && <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg p-3">{success}</p>}

      <div className="bg-white rounded-xl shadow-lg">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Device</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Serial</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Location</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {devices.map((d) => (
              <tr
                key={d.serialNumber}
                className="hover:bg-gray-50"
                style={openActionMenu === d.serialNumber ? { position: 'relative', zIndex: 30 } : undefined}
              >
                <td className="p-3 font-medium text-gray-900">{d.name}</td>
                <td className="p-3 text-sm text-gray-500 font-mono">{d.serialNumber}</td>
                <td className="p-3 text-sm text-gray-500">{d.location}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${d.isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    <FaCircle size={6} className={d.isOnline ? 'text-green-500' : 'text-gray-400'} />
                    {d.isOnline ? 'Online' : 'Offline'} · {timeSince(d.lastSeen)}
                  </span>
                </td>
                <td className="p-3 relative">
                  <button
                    onClick={() => setOpenActionMenu(openActionMenu === d.serialNumber ? null : d.serialNumber)}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Actions <FaEllipsisV size={10} />
                  </button>

                  {openActionMenu === d.serialNumber && (
                    <>
                      {/* Backdrop closes the menu on an outside click */}
                      <div className="fixed inset-0 z-10" onClick={() => setOpenActionMenu(null)} />
                      <div className="absolute right-3 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        <button onClick={() => { openEnroll(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaFingerprint size={13} className="text-purple-600" /> Enroll Fingerprint
                        </button>
                        <button onClick={() => { openSync(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaSync size={13} className="text-blue-600" /> Sync Staff
                        </button>
                        <button onClick={() => { openDeviceData(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaDatabase size={13} className="text-indigo-600" /> View Device Data
                        </button>
                        <button onClick={() => { openQueue(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaListUl size={13} className="text-gray-600" /> Command Queue
                        </button>
                        <button onClick={() => { handleSyncTime(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaClock size={13} className="text-teal-600" /> Sync Time
                        </button>
                        <button onClick={() => { handleReboot(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <FaPowerOff size={13} className="text-amber-600" /> Reboot Device
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                        <button onClick={() => { handleDeleteDevice(d); setOpenActionMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                          <FaTrash size={13} /> Delete Device
                        </button>
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gray-400">No devices registered yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Device Modal */}
      {showAddDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleAddDevice} className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add Device</h3>
              <button type="button" onClick={() => setShowAddDevice(false)} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
            <input
              required
              value={newDevice.serialNumber}
              onChange={(e) => setNewDevice({ ...newDevice, serialNumber: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-2 mb-3"
              placeholder="e.g. AXXH200860003"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={newDevice.name}
              onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-2 mb-3"
              placeholder="e.g. Front Desk"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              value={newDevice.location}
              onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-2 mb-4"
              placeholder="e.g. Salon Reception"
            />
            <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">Add Device</button>
          </form>
        </div>
      )}

      {/* Enroll Fingerprint Modal */}
      {enrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Enroll Fingerprint</h3>
              <button onClick={closeEnroll} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Device: {enrollModal.device.name} ({enrollModal.device.serialNumber})</p>

            {enrollState === 'idle' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
                <select
                  value={enrollEmployeeId}
                  onChange={(e) => setEnrollEmployeeId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 mb-3"
                >
                  <option value="">Select staff...</option>
                  {(staff || []).map((s) => (
                    <option key={s._id} value={s.employeeId}>{s.firstName} {s.lastName} (ID: {s.employeeId})</option>
                  ))}
                </select>

                <label className="block text-sm font-medium text-gray-700 mb-1">Which finger?</label>
                <select
                  value={enrollFingerId}
                  onChange={(e) => setEnrollFingerId(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg p-2 mb-4"
                >
                  {FINGER_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>

                <p className="text-xs text-gray-500 mb-4">
                  If this is the staff member's first time on this device, sync them first (Sync Staff) — enrollment fails for anyone not already recognized by the device.
                </p>

                <button
                  onClick={sendEnroll}
                  disabled={!enrollEmployeeId}
                  className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  Send to Device
                </button>
              </>
            )}

            {(enrollState === 'sending' || enrollState === 'pending') && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
                {enrollMessage || 'Sending command...'}
              </div>
            )}
            {enrollState === 'acknowledged' && <p className="text-sm text-green-700 font-medium">{enrollMessage}</p>}
            {(enrollState === 'failed' || enrollState === 'error') && <p className="text-sm text-red-600 font-medium">{enrollMessage}</p>}

            {enrollState !== 'idle' && (
              <button onClick={closeEnroll} className="w-full mt-4 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">Close</button>
            )}
          </div>
        </div>
      )}

      {/* Sync Staff Modal */}
      {syncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Sync Staff</h3>
              <button onClick={() => setSyncModal(null)} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Device: {syncModal.device.name} ({syncModal.device.serialNumber})</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Sync one staff member</label>
            <div className="flex gap-2 mb-4">
              <select
                value={syncEmployeeId}
                onChange={(e) => setSyncEmployeeId(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg p-2"
              >
                <option value="">Select staff...</option>
                {(staff || []).map((s) => (
                  <option key={s._id} value={s.employeeId}>{s.firstName} {s.lastName} (ID: {s.employeeId})</option>
                ))}
              </select>
              <button onClick={handleSyncOne} disabled={!syncEmployeeId || syncBusy} className="px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                Sync
              </button>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <button onClick={handleSyncAll} disabled={syncBusy} className="w-full flex items-center justify-center gap-2 bg-blue-50 text-blue-700 py-2 rounded-lg hover:bg-blue-100 disabled:opacity-50">
                <FaSyncAlt size={12} /> Sync All Active Staff
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Command Queue Modal */}
      {queueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Command Queue — {queueModal.device.name}</h3>
              <button onClick={() => setQueueModal(null)} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
            </div>
            {queueModal.queue.length === 0 ? (
              <p className="text-sm text-gray-400">No commands yet.</p>
            ) : (
              <div className="space-y-2">
                {queueModal.queue.map((cmd) => (
                  <div key={cmd._id} className="border border-gray-100 rounded-lg p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">{cmd.commandType}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        cmd.status === 'acknowledged' ? 'bg-green-100 text-green-700' :
                        cmd.status === 'failed' ? 'bg-red-100 text-red-700' :
                        cmd.status === 'sent' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                      }`}>{cmd.status}</span>
                    </div>
                    <p className="text-xs text-gray-500 font-mono mt-1">{cmd.rawCommand}</p>
                    {cmd.deviceResponse && <p className="text-xs text-gray-500 mt-1">Response: {cmd.deviceResponse}</p>}
                    <p className="text-xs text-gray-400 mt-1">Queued {timeSince(cmd.createdAt)} by {cmd.createdBy}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Device Data Modal */}
      {dataModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold text-gray-900">Device Data — {dataModal.device.name}</h3>
              <button onClick={closeDeviceData} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              The actual roster currently on this device — including anyone enrolled directly at the keypad.
            </p>

            {deviceData.status === 'requesting' && !deviceData.isIdle && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                Retrieving... ({deviceData.users.length} user(s) so far)
              </div>
            )}

            {deviceData.users.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={selectedPins.length === deviceData.users.length} onChange={toggleSelectAllPins} />
                    Select all ({deviceData.users.length})
                  </label>
                  <select value={importSubBusiness} onChange={(e) => setImportSubBusiness(e.target.value)} className="border border-gray-300 rounded-lg p-1.5 text-sm">
                    {SUB_BUSINESS_OPTIONS.map((sb) => <option key={sb} value={sb}>{sb}</option>)}
                  </select>
                </div>

                <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 mb-4 max-h-80 overflow-y-auto">
                  {deviceData.users.map((u) => (
                    <div key={u.pin} className="flex items-center gap-3 p-2.5">
                      <input type="checkbox" checked={selectedPins.includes(u.pin)} onChange={() => togglePin(u.pin)} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{u.name || '(no name)'} — PIN {u.pin}</p>
                        <p className="text-xs text-gray-500">
                          {u.hasFingerprint ? `${u.fingerprintCount} fingerprint(s)` : 'No fingerprint'}
                          {u.hasFace ? ', has face' : ''}
                          {u.hasPhoto ? ', has photo' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button onClick={handleImportSelected} disabled={selectedPins.length === 0 || dataBusy} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    Import Selected to Staff
                  </button>
                  <button onClick={handleDeleteSelectedFromDevice} disabled={selectedPins.length === 0 || dataBusy} className="flex-1 bg-red-50 text-red-700 py-2 rounded-lg hover:bg-red-100 disabled:opacity-50">
                    Delete Selected from Device
                  </button>
                </div>
              </>
            )}

            {deviceData.status !== 'requesting' && deviceData.users.length === 0 && (
              <p className="text-sm text-gray-400">No users found on this device.</p>
            )}

            {importResults && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Import Results</h4>
                <div className="space-y-1">
                  {importResults.map((r) => (
                    <p key={r.pin} className="text-xs text-gray-600">
                      PIN {r.pin}: {r.outcome === 'created' ? `created as ${r.name}` : r.outcome === 'conflict' ? `conflict — employeeId already belongs to ${r.existing?.name}` : r.message}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceManagement;
